// LT-643 — Campaign Completion Report tab.
//
// Renders the persisted completion report: deterministic metric scaffold
// (overview, KPI scorecard, call outcomes, captured data, priority contacts)
// + the LLM narrative. Numbers come only from the payload — never recomputed
// here. "Download PDF" generates the report client-side (PII warning first).
//
// States keyed off payload.status: none (not terminal yet) / pending
// (generating, polled) / ready / failed.
import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Download, FileText, Info, RefreshCw } from 'lucide-react';

import { Button, Callout, Pill } from '../../../ui';
import {
  campaignsAPI,
  extractCampaignApiError,
  type CompletionReportKpi,
  type CompletionReportPayload,
} from '../../../services/campaignsAPI';
import { downloadCompletionReportPdf } from './completionReportPdf';

interface Props {
  campaignId: string;
}

function fmtDate(iso?: string | null, withTime = false): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString();
  return withTime
    ? `${date} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : date;
}

const Md: React.FC<{ children: string }> = ({ children }) => (
  <div className="text-sm text-slate-300 leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_strong]:text-slate-100 [&_strong]:font-semibold">
    <ReactMarkdown>{children}</ReactMarkdown>
  </div>
);

const MetricTile: React.FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <div className="rounded-ds-md bg-white/[0.03] border border-white/[0.06] px-3 py-2.5">
    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">
      {label}
    </div>
    <div className="text-lg font-semibold text-slate-100">{value}</div>
    {sub ? <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div> : null}
  </div>
);

const KpiBar: React.FC<{ kpi: CompletionReportKpi }> = ({ kpi }) => {
  const pct =
    kpi.target_value > 0
      ? Math.min((kpi.current_value / kpi.target_value) * 100, 100)
      : 0;
  const bar = !kpi.computed
    ? 'bg-slate-500'
    : kpi.attained
      ? 'bg-ok'
      : 'bg-danger';
  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-200">{kpi.name}</span>
        <span className="flex items-center gap-2">
          <span className="font-mono text-slate-400">
            {kpi.computed ? `${kpi.current_value}${kpi.unit}` : 'n/a'}
            {' / '}
            {kpi.target_value}
            {kpi.unit}
          </span>
          {kpi.attained === null ? (
            <Pill intent="neutral">no data</Pill>
          ) : kpi.attained ? (
            <Pill intent="ok">met</Pill>
          ) : (
            <Pill intent="danger">missed</Pill>
          )}
        </span>
      </div>
      <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
        <div
          className={`${bar} h-full opacity-80`}
          style={{ width: `${Math.max(pct, 0)}%` }}
        />
      </div>
    </div>
  );
};

const DistList: React.FC<{ title: string; data: Record<string, number> }> = ({
  title,
  data,
}) => {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        {title}
      </div>
      <ul className="flex flex-col gap-1">
        {entries.map(([k, v]) => (
          <li key={k} className="flex items-center justify-between text-xs">
            <span className="text-slate-300">{k.replace(/_/g, ' ')}</span>
            <span className="font-mono text-slate-400">{v.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-sm font-semibold text-slate-100 mb-2 mt-1">{children}</h3>
);

const CampaignCompletionReport: React.FC<Props> = ({ campaignId }) => {
  const [report, setReport] = useState<CompletionReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    campaignsAPI
      .completionReport(campaignId)
      .then(setReport)
      .catch((err) =>
        setError(extractCampaignApiError(err, 'Failed to load completion report.')),
      )
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(load, [load]);

  // Poll while the report is still being generated by the backend poller.
  useEffect(() => {
    if (report?.status !== 'pending' && report?.status !== 'generating') {
      return undefined;
    }
    const t = setInterval(() => {
      campaignsAPI
        .completionReport(campaignId)
        .then(setReport)
        .catch(() => undefined);
    }, 5000);
    return () => clearInterval(t);
  }, [report?.status, campaignId]);

  const handleRegenerate = useCallback(() => {
    setRegenerating(true);
    setActionError(null);
    campaignsAPI
      .regenerateCompletionReport(campaignId)
      .then(setReport)
      .catch((err) =>
        setActionError(extractCampaignApiError(err, 'Failed to regenerate report.')),
      )
      .finally(() => setRegenerating(false));
  }, [campaignId]);

  const handleDownload = useCallback(() => {
    if (!report || report.status !== 'ready') return;
    const ok = window.confirm(
      'This report includes contact names and phone numbers (personal data). ' +
        'Download the PDF?',
    );
    if (!ok) return;
    downloadCompletionReportPdf(report).catch(() =>
      setActionError('Failed to generate the PDF.'),
    );
  }, [report]);

  if (loading) {
    return <div className="text-sm text-slate-500 py-6">Loading report…</div>;
  }

  if (error) {
    return (
      <div className="px-3 py-2 rounded-ds-lg border border-danger-border bg-danger-tint text-danger-text text-sm">
        {error}
      </div>
    );
  }

  const status = report?.status ?? 'none';

  if (status === 'none') {
    return (
      <div className="border border-dashed border-white/[0.12] rounded-ds-md bg-white/[0.02] py-12 text-center">
        <FileText size={20} className="mx-auto text-slate-500 mb-2" />
        <div className="text-sm text-slate-200 font-medium mb-1">
          No completion report yet
        </div>
        <div className="text-xs text-slate-500 max-w-md mx-auto">
          A report is generated automatically once the campaign completes or is
          ended.
        </div>
      </div>
    );
  }

  if (status === 'pending' || status === 'generating') {
    return (
      <div className="border border-dashed border-white/[0.12] rounded-ds-md bg-white/[0.02] py-12 text-center">
        <RefreshCw size={20} className="mx-auto text-slate-500 mb-2 animate-spin" />
        <div className="text-sm text-slate-200 font-medium mb-1">
          Generating report…
        </div>
        <div className="text-xs text-slate-500 max-w-md mx-auto">
          Computing metrics and writing the summary. This refreshes
          automatically.
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div>
        <Callout tone="danger" title="Report generation failed">
          {report?.error || 'Something went wrong while generating the report.'}
        </Callout>
        <div className="mt-3">
          <Button
            intent="secondary"
            onClick={handleRegenerate}
            disabled={regenerating}
          >
            <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </div>
        {actionError ? (
          <div className="mt-2 text-xs text-danger-text">{actionError}</div>
        ) : null}
      </div>
    );
  }

  // status === 'ready'
  const campaign = report?.campaign;
  const metrics = report?.metrics;
  const narrative = report?.narrative;
  const ov = metrics?.overview;

  return (
    <div className="flex flex-col gap-5">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-slate-500">
          {report?.generated_at
            ? `Generated ${new Date(report.generated_at).toLocaleString()}`
            : null}
          {report?.narrative_model ? (
            <span className="ml-2 font-mono text-slate-600">
              · {report.narrative_model}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button intent="secondary" onClick={handleDownload}>
            <Download size={14} /> Download PDF
          </Button>
          <Button
            intent="ghost"
            onClick={handleRegenerate}
            disabled={regenerating}
          >
            <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </div>
      </div>

      {actionError ? (
        <div className="text-xs text-danger-text">{actionError}</div>
      ) : null}

      {/* Campaign brief + run window */}
      {campaign ? (
        <div className="rounded-ds-md bg-white/[0.03] border border-white/[0.06] px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
            Campaign
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400 mb-2">
            <span>
              <span className="text-slate-500">Status </span>
              {(campaign.status || '').replace(/_/g, ' ')}
            </span>
            <span>
              <span className="text-slate-500">Run </span>
              {fmtDate(campaign.launched_at)} – {fmtDate(campaign.ended_at)}
            </span>
            <span>
              <span className="text-slate-500">Generated </span>
              {fmtDate(report?.generated_at, true)}
            </span>
          </div>
          {campaign.purpose ? (
            <p className="text-sm text-slate-300 mb-1">
              <span className="text-slate-500">Purpose: </span>
              {campaign.purpose}
            </p>
          ) : null}
          {campaign.target_audience ? (
            <p className="text-sm text-slate-300 mb-1">
              <span className="text-slate-500">Audience: </span>
              {campaign.target_audience}
            </p>
          ) : null}
          {campaign.campaign_goals ? (
            <p className="text-sm text-slate-300">
              <span className="text-slate-500">Goal: </span>
              {campaign.campaign_goals}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* PII banner */}
      <Callout tone="warn" title="Contains personal data">
        This report includes contact names and phone numbers so you can act on
        priority follow-ups. Handle the PDF accordingly.
      </Callout>

      {report?.narrative_model === null ? (
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <Info size={12} /> AI summary unavailable — showing the computed
          summary.
        </div>
      ) : null}

      {campaign?.status === 'force_ended' ? (
        <Callout tone="warn" title="Campaign ended early">
          {campaign.end_reason
            ? `Reason: ${campaign.end_reason}`
            : 'This campaign was force-ended before completing.'}
        </Callout>
      ) : null}

      {/* Narrative: headline + executive summary */}
      {narrative?.headline ? (
        <p className="text-base font-semibold text-slate-50">
          {narrative.headline}
        </p>
      ) : null}
      {narrative?.executive_summary ? (
        <Md>{narrative.executive_summary}</Md>
      ) : null}

      {/* Overview tiles */}
      {ov ? (
        <div>
          <SectionHeading>Overview</SectionHeading>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <MetricTile label="Enrolled" value={ov.total_enrolments.toLocaleString()} />
            <MetricTile
              label="Connected"
              value={ov.calls_connected.toLocaleString()}
              sub={`${ov.calls_attempted.toLocaleString()} attempted · ${ov.connect_rate_pct}%`}
            />
            <MetricTile
              label="Qualified"
              value={ov.qualified_count.toLocaleString()}
              sub={`${ov.conversion_rate_pct}% conversion`}
            />
            <MetricTile label="Opted out" value={ov.opted_out.toLocaleString()} />
            <MetricTile
              label="Callbacks"
              value={ov.callbacks_requested.toLocaleString()}
            />
            <MetricTile
              label="Pipeline"
              value={ov.pipeline_dollars.toLocaleString()}
            />
          </div>
        </div>
      ) : null}

      {/* KPI scorecard */}
      {metrics && metrics.kpi_scorecard.length > 0 ? (
        <div>
          <SectionHeading>KPI scorecard</SectionHeading>
          <div className="flex flex-col gap-1">
            {metrics.kpi_scorecard.map((k) => (
              <KpiBar key={k.name} kpi={k} />
            ))}
          </div>
        </div>
      ) : null}

      {/* Call outcomes */}
      {metrics ? (
        <div>
          <SectionHeading>Call outcomes</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DistList title="By disposition" data={metrics.call_outcomes.by_disposition} />
            <DistList title="By temperature" data={metrics.call_outcomes.by_temperature} />
            <DistList title="By goal outcome" data={metrics.call_outcomes.by_goal_outcome} />
            <div className="flex flex-col gap-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                Engagement &amp; cost
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-300">Avg engagement</span>
                <span className="font-mono text-slate-400">
                  {metrics.call_outcomes.avg_engagement ?? '—'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-300">Total call cost</span>
                <span className="font-mono text-slate-400">
                  {metrics.call_outcomes.total_call_cost.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Definitions glossary */}
      {metrics ? (
        <details className="rounded-ds-md bg-white/[0.02] border border-white/[0.06] px-4 py-3">
          <summary className="text-xs text-slate-300 cursor-pointer select-none">
            What do “disposition” and “temperature” mean?
          </summary>
          <div className="mt-3 flex flex-col gap-2 text-xs text-slate-400">
            <p>
              <span className="text-slate-200 font-medium">Disposition</span> — the
              classified outcome of each call. Connected calls get a conversation
              result; calls that didn’t connect get a reason.
            </p>
            <ul className="list-disc pl-5 flex flex-col gap-0.5">
              <li><span className="text-slate-300">Qualified:</span> converted, interested_meeting, interested_referred</li>
              <li><span className="text-slate-300">Callback:</span> interested_callback</li>
              <li><span className="text-slate-300">Nurture:</span> interested_future, interested_info, soft_rejection, not_available</li>
              <li><span className="text-slate-300">Closed out:</span> hard_rejection</li>
              <li><span className="text-slate-300">Not reached:</span> no_answer, voicemail, busy, wrong_number, gatekeeper_block</li>
            </ul>
            <p className="mt-1">
              <span className="text-slate-200 font-medium">Lead temperature</span> — the
              AI’s read of how promising the lead is after the call, coldest to
              hottest: <span className="text-slate-300">dead, cold, lukewarm, warm, hot</span>.
            </p>
          </div>
        </details>
      ) : null}

      {/* Captured data rollups */}
      {metrics && metrics.captured_data.length > 0 ? (
        <div>
          <SectionHeading>Captured data</SectionHeading>
          <ul className="flex flex-col gap-2">
            {metrics.captured_data.map((f) => {
              const rollup = f.rollup as Record<string, unknown>;
              const items = Array.isArray(rollup.items)
                ? (rollup.items as Array<Record<string, unknown>>)
                : [];
              const declared = (Array.isArray(rollup.item_columns)
                ? rollup.item_columns
                : []) as Array<{ key: string; label: string }>;
              const cols =
                declared.length > 0
                  ? declared
                  : Array.from(
                      items.reduce<Set<string>>((acc, it) => {
                        Object.keys(it).forEach((k) => acc.add(k));
                        return acc;
                      }, new Set()),
                    ).map((k) => ({ key: k, label: k }));
              const totalItems = typeof rollup.total_items === 'number' ? rollup.total_items : null;
              return (
                <li key={f.key} className="flex flex-col gap-1 border-b border-white/[0.04] pb-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-300">{f.label}</span>
                    <span className="font-mono text-slate-400">
                      {totalItems !== null ? `${totalItems} items · ` : ''}
                      {String(rollup.filled ?? 0)}/{String(rollup.total_calls ?? 0)} filled
                    </span>
                  </div>
                  {items.length > 0 ? (
                    <div className="overflow-x-auto rounded-md border border-slate-700/50">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-800/40">
                            {cols.map((c) => (
                              <th
                                key={c.key}
                                className="text-left px-2 py-1 text-[11px] font-medium text-slate-400 uppercase tracking-wider"
                              >
                                {c.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it, i) => (
                            <tr key={i} className="border-t border-slate-700/40">
                              {cols.map((c) => (
                                <td key={c.key} className="px-2 py-1 text-slate-300">
                                  {it[c.key] == null ? '' : String(it[c.key])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {rollup.items_truncated === true && totalItems !== null ? (
                        <div className="px-2 py-1 text-[11px] text-slate-500">
                          Showing {items.length} of {totalItems} items.
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {/* Narrative detail sections */}
      {narrative ? (
        <div className="flex flex-col gap-4">
          {narrative.kpi_assessment ? (
            <div>
              <SectionHeading>KPI assessment</SectionHeading>
              <Md>{narrative.kpi_assessment}</Md>
            </div>
          ) : null}
          {narrative.what_worked ? (
            <div>
              <SectionHeading>What worked</SectionHeading>
              <Md>{narrative.what_worked}</Md>
            </div>
          ) : null}
          {narrative.what_to_improve ? (
            <div>
              <SectionHeading>What to improve</SectionHeading>
              <Md>{narrative.what_to_improve}</Md>
            </div>
          ) : null}
          {narrative.recommended_next_steps ? (
            <div>
              <SectionHeading>Recommended next steps</SectionHeading>
              <Md>{narrative.recommended_next_steps}</Md>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Priority contacts (PII) */}
      {metrics && metrics.priority_contacts.length > 0 ? (
        <div>
          <SectionHeading>Priority follow-up contacts</SectionHeading>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="font-medium py-1.5 pr-2">Name</th>
                  <th className="font-medium py-1.5 pr-2">Phone</th>
                  <th className="font-medium py-1.5 pr-2">Temp</th>
                  <th className="font-medium py-1.5 pr-2">Disposition</th>
                  <th className="font-medium py-1.5">Follow-up</th>
                </tr>
              </thead>
              <tbody>
                {metrics.priority_contacts.map((c, i) => (
                  <tr key={`${c.phone ?? c.name}-${i}`} className="border-t border-white/[0.04]">
                    <td className="py-1.5 pr-2 text-slate-200">{c.name}</td>
                    <td className="py-1.5 pr-2 font-mono text-slate-400">
                      {c.phone || '—'}
                    </td>
                    <td className="py-1.5 pr-2 text-slate-300">
                      {c.temperature || '—'}
                    </td>
                    <td className="py-1.5 pr-2 text-slate-300">
                      {(c.disposition || '—').replace(/_/g, ' ')}
                    </td>
                    <td className="py-1.5 text-slate-300">
                      {c.follow_up_action_type
                        ? `${c.follow_up_action_type}${
                            c.follow_up_description
                              ? `: ${c.follow_up_description}`
                              : ''
                          }`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CampaignCompletionReport;
